import { createSignal, For, Show } from "solid-js";
import { linesEntered as lines } from "../../boundaries/user-list-input.parser";
import type { BriefEditSection } from "../../enums/brief";
import { BRIEF_EDIT_SECTION } from "../../enums/brief";
import type {
  BriefQuestion,
  BriefRequirementBlock,
  DeliveryBrief,
} from "./briefTypes";

export function BriefEditForm(props: {
  brief: DeliveryBrief;
  section: BriefEditSection;
  onSave: (patch: Partial<DeliveryBrief>) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = createSignal(props.brief.title);
  const [owner, setOwner] = createSignal(props.brief.owner);
  const [objective, setObjective] = createSignal(props.brief.objective);
  const [requiredChange, setRequiredChange] = createSignal(
    props.brief.requiredChange,
  );
  const [scopeNotes, setScopeNotes] = createSignal(props.brief.scopeNotes);
  const [outOfScopeNotes, setOutOfScopeNotes] = createSignal(
    props.brief.outOfScopeNotes,
  );
  const [requirements, setRequirements] = createSignal(
    props.brief.requirements.map((block) => ({
      ...block,
      itemsText: block.items.join("\n"),
    })),
  );
  const [acceptance, setAcceptance] = createSignal(
    props.brief.acceptanceCriteria.join("\n"),
  );
  const [questions, setQuestions] = createSignal(
    props.brief.openQuestions.map((question) => question.text).join("\n"),
  );

  const save = (event: SubmitEvent) => {
    event.preventDefault();
    props.onSave(patchForSection());
  };
  const patchForSection = (): Partial<DeliveryBrief> => {
    if (props.section === BRIEF_EDIT_SECTION.IDENTITY) {
      return { title: title().trim(), owner: owner().trim() || "Not assigned" };
    }
    if (props.section === BRIEF_EDIT_SECTION.OBJECTIVE)
      return { objective: objective().trim() };
    if (props.section === BRIEF_EDIT_SECTION.REQUIRED_CHANGE) {
      return { requiredChange: requiredChange().trim() };
    }
    if (props.section === BRIEF_EDIT_SECTION.SCOPE) {
      return {
        scopeNotes: scopeNotes().trim(),
        outOfScopeNotes: outOfScopeNotes().trim(),
      };
    }
    if (props.section === BRIEF_EDIT_SECTION.REQUIREMENTS) {
      return { requirements: cleanedRequirements(requirements()) };
    }
    if (props.section === BRIEF_EDIT_SECTION.ACCEPTANCE) {
      return { acceptanceCriteria: lines(acceptance()) };
    }
    return {
      openQuestions: editedQuestions(props.brief.openQuestions, questions()),
    };
  };

  return (
    <form class="brief-edit-form" onSubmit={save}>
      <Show when={props.section === BRIEF_EDIT_SECTION.IDENTITY}>
        <TextArea
          label="Brief title"
          value={title()}
          rows={4}
          onInput={setTitle}
        />
        <TextField
          label="Owner or responsible team"
          value={owner() === "Not assigned" ? "" : owner()}
          placeholder="Not assigned"
          onInput={setOwner}
        />
        <p class="brief-edit-note">
          Delivery status is changed through the readiness actions after the
          required inputs are complete.
        </p>
      </Show>
      <Show when={props.section === BRIEF_EDIT_SECTION.OBJECTIVE}>
        <TextArea
          label="Objective"
          value={objective()}
          rows={6}
          onInput={setObjective}
        />
      </Show>
      <Show when={props.section === BRIEF_EDIT_SECTION.REQUIRED_CHANGE}>
        <TextArea
          label="Required change"
          value={requiredChange()}
          rows={8}
          onInput={setRequiredChange}
        />
      </Show>
      <Show when={props.section === BRIEF_EDIT_SECTION.SCOPE}>
        <TextArea
          label="Scope notes"
          value={scopeNotes()}
          rows={5}
          onInput={setScopeNotes}
        />
        <TextArea
          label="Out-of-scope notes"
          value={outOfScopeNotes()}
          rows={5}
          onInput={setOutOfScopeNotes}
        />
      </Show>
      <Show when={props.section === BRIEF_EDIT_SECTION.REQUIREMENTS}>
        <For each={requirements()}>
          {(block, index) => (
            <div class="brief-edit-block">
              <TextField
                label={`Requirement block ${index() + 1}`}
                value={block.title}
                onInput={(value) =>
                  setRequirements((current) =>
                    current.map((item, row) =>
                      row === index() ? { ...item, title: value } : item,
                    ),
                  )
                }
              />
              <TextArea
                label="Requirements, one per line"
                value={block.itemsText}
                rows={6}
                onInput={(value) =>
                  setRequirements((current) =>
                    current.map((item, row) =>
                      row === index() ? { ...item, itemsText: value } : item,
                    ),
                  )
                }
              />
            </div>
          )}
        </For>
        <Show when={requirements().length === 0}>
          <p class="brief-edit-note">
            Faro does not have enough supported evidence to prefill
            requirements.
          </p>
        </Show>
      </Show>
      <Show when={props.section === BRIEF_EDIT_SECTION.ACCEPTANCE}>
        <TextArea
          label="Acceptance criteria, one per line"
          value={acceptance()}
          rows={10}
          onInput={setAcceptance}
        />
      </Show>
      <Show when={props.section === BRIEF_EDIT_SECTION.QUESTIONS}>
        <TextArea
          label="Open questions, one per line"
          value={questions()}
          rows={10}
          onInput={setQuestions}
        />
      </Show>
      <div class="brief-edit-actions">
        <button type="button" onClick={props.onCancel}>
          Cancel
        </button>
        <button type="submit" class="brief-primary-action">
          Apply preview change
        </button>
      </div>
    </form>
  );
}

function TextField(props: {
  label: string;
  value: string;
  placeholder?: string;
  onInput: (value: string) => void;
}) {
  return (
    <label>
      <span>{props.label}</span>
      <input
        value={props.value}
        placeholder={props.placeholder}
        onInput={(event) => props.onInput(event.currentTarget.value)}
      />
    </label>
  );
}

function TextArea(props: {
  label: string;
  value: string;
  rows: number;
  onInput: (value: string) => void;
}) {
  return (
    <label>
      <span>{props.label}</span>
      <textarea
        rows={props.rows}
        value={props.value}
        onInput={(event) => props.onInput(event.currentTarget.value)}
      />
    </label>
  );
}

function cleanedRequirements(
  blocks: Array<BriefRequirementBlock & { itemsText: string }>,
): BriefRequirementBlock[] {
  return blocks
    .map(({ itemsText, ...block }) => ({
      ...block,
      title: block.title.trim(),
      items: lines(itemsText),
    }))
    .filter((block) => block.title && block.items.length > 0);
}

function editedQuestions(
  current: BriefQuestion[],
  value: string,
): BriefQuestion[] {
  return lines(value).map((text, index) => {
    const existing = current.find((question) => question.text === text);
    return (
      existing ?? {
        id: `preview-question-${index}`,
        text,
        response: "",
        confirmationSource: "",
      }
    );
  });
}
